import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  DocumentTextIcon,
  PlusIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import {
  fetchTextDocumentPdf,
  fetchTextDocuments,
} from "../../../api/textDocuments";
import NewTextDocumentDialog from "./NewTextDocumentDialog";

function statusBadge(status) {
  return status === "final"
    ? "bg-emerald-100 text-emerald-800"
    : "bg-amber-100 text-amber-800";
}

/**
 * Embeddable list of text documents (letters / agreements) attached to a subject
 * (employee, customer, project). Creating opens the full editor pre-linked to the subject.
 */
export default function TextDocumentsSection({ subjectType, subjectId, title, subtitle }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    if (!subjectType || subjectId == null) return;
    setIsLoading(true);
    const res = await fetchTextDocuments({
      subject_type: subjectType,
      subject_id: subjectId,
      per_page: 100,
    });
    setDocs(res?.data || []);
    setIsLoading(false);
  }, [subjectType, subjectId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreated = (doc) => {
    setCreateOpen(false);
    if (doc?.id) {
      navigate(`/dashboard/text-documents/${doc.id}`);
    }
  };

  const handlePrint = async (doc) => {
    try {
      const res = await fetchTextDocumentPdf(doc.id, { signature: doc.include_signature });
      if (res?.blob) {
        const url = window.URL.createObjectURL(res.blob);
        window.open(url, "_blank", "noopener");
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
        return;
      }
      toast.error(t("textDocuments.print_failed"));
    } catch {
      toast.error(t("textDocuments.print_failed"));
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            {title || t("textDocuments.title")}
          </h3>
          <p className="text-sm text-slate-500">
            {subtitle || t("textDocuments.section_subtitle")}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
        >
          <PlusIcon className="h-4 w-4" />
          {t("textDocuments.new")}
        </button>
      </div>

      <div className="p-4">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-slate-600" />
          </div>
        ) : docs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 px-6 py-10 text-center">
            <DocumentTextIcon className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-2 text-sm font-medium text-slate-700">
              {t("textDocuments.empty")}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {docs.map((doc) => (
              <li
                key={doc.id}
                className="flex items-center justify-between py-3"
              >
                <button
                  type="button"
                  onClick={() => navigate(`/dashboard/text-documents/${doc.id}`)}
                  className="flex min-w-0 flex-1 items-center gap-3 text-left"
                >
                  <DocumentTextIcon className="h-5 w-5 flex-shrink-0 text-slate-400" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-semibold text-slate-900">
                      {doc.title}
                    </span>
                  </span>
                </button>
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${statusBadge(
                      doc.status
                    )}`}
                  >
                    {t(`textDocuments.status.${doc.status}`)}
                  </span>
                  <button
                    type="button"
                    title={t("textDocuments.print")}
                    onClick={() => handlePrint(doc)}
                    className="rounded-full p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
                  >
                    <PrinterIcon className="h-4 w-4" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <NewTextDocumentDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        subjectType={subjectType}
        subjectId={subjectId}
        onCreated={handleCreated}
      />
    </div>
  );
}
